import './linkLibraryDonationLinks.scss';

import {
  AcceptedCurrencyCode,
  useInfiniteDonationLinksQuery,
} from '../../../types/__generated-graphQL__';
import React, { FunctionComponent, useContext, useState } from 'react';

import { AuthContext } from '@justgiving/auth-react';
import DonationLinkArchiveModal from '../DonationLinkArchiveModal/DonationLinkArchiveModal';
import DonationLinkRestoreModal from '../DonationLinkRestoreModal/DonationLinkRestoreModal';
import DonationLinkViewModal from '../DonationLinkViewModal/DonationLinkViewModal';
import LinkLibraryActionsMenu from './LinkLibraryActionsMenu';
import Loader from '@justgiving/loading';
import { PlaceholderBar } from '../PlaceholderBar/PlaceholderBar';
import Refresh from '../Refresh/Refresh';
import SimpleSharableLinkModal from '../SimpleShareableLink/SimpleShareableLinkModal';
import classNames from 'classnames';
import { createGraphQlClient } from '../../graphQl/baseApi';
import formatDate from '../../utils/formatDate';
import getTotalRaised from '../../utils/getTotalRaised';
import { useCharityData } from '../../context/charityContext';
import { useDonationLinkContext } from '../../context/donationLinkContext';
import { useTranslation } from 'react-i18next';

type DonationLinkItemProps = {
  label?: string;
  value?: string;
  className?: string;
  placeholderWidth?: string;
  isDefault?: boolean;
};

const DonationLinkItem: FunctionComponent<DonationLinkItemProps> = ({
  label = '',
  value = '',
  className = '',
  placeholderWidth = '100%',
  isDefault = false,
}) => {
  const isLoading = !label && !value;
  return (
    <li className={`donationLinks__row__item ${className}`}>
      <span className="dl-label" aria-hidden>
        {isLoading ? <PlaceholderBar animated /> : label}
      </span>
      <span
        className={classNames('dl-value', {
          'dl-value--default': isDefault,
        })}
      >
        {isLoading ? (
          <PlaceholderBar animated width={placeholderWidth} />
        ) : (
          <>
            <span aria-hidden>{value}</span>
            <span className="jg-sr-only">
              {label} {value}
            </span>
          </>
        )}
      </span>
    </li>
  );
};

const DonationLinksLoading: FunctionComponent<{ isError?: boolean }> = ({
  isError = false,
}) => {
  const classnames = classNames('donationLinks__row', {
    'donationLinks__row--isError': isError,
  });
  return (
    <>
      <ul className={classnames}>
        <DonationLinkItem className="dl-name" placeholderWidth="90%" />
        <DonationLinkItem placeholderWidth="55%" />
        <DonationLinkItem placeholderWidth="60%" />
        <DonationLinkItem placeholderWidth="80%" />
        <DonationLinkItem placeholderWidth="30%" />
        <DonationLinkItem placeholderWidth="70%" />
        <DonationLinkItem className="dl-actions" />
      </ul>

      <ul className={classnames}>
        <DonationLinkItem className="dl-name" placeholderWidth="75%" />
        <DonationLinkItem placeholderWidth="40%" />
        <DonationLinkItem placeholderWidth="70%" />
        <DonationLinkItem placeholderWidth="90%" />
        <DonationLinkItem placeholderWidth="60%" />
        <DonationLinkItem placeholderWidth="90%" />
        <DonationLinkItem className="dl-actions" />
      </ul>
    </>
  );
};

type DonationLinkRowsProps = {
  showPlaceholderDuringLoad?: boolean;
  showArchived: boolean;
};

const DonationLinkRows: FunctionComponent<DonationLinkRowsProps> = ({
  showPlaceholderDuringLoad = true,
  showArchived,
}) => {
  const { t } = useTranslation();
  const { charity } = useCharityData();
  const auth = useContext(AuthContext);

  const { isError, data, refetch } = useInfiniteDonationLinksQuery(
    createGraphQlClient(auth),
    {
      input: { first: 4, archived: showArchived },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) =>
        lastPage?.donationLinks?.pageInfo.endCursor,
    },
  );

  if (data?.pages) {
    return (
      <ul
        className="donationLinks__rows"
        title={t('linkLibrary.donationLinks.listTitle')}
      >
        {data?.pages.map((group, i) => {
          return (
            <React.Fragment key={i}>
              {group.donationLinks?.edges.map(({ node }) => {
                const {
                  name,
                  createDate,
                  parameters: { recurring, reference },
                  donationLinkId,
                  author,
                  totalRaised,
                  isDefault,
                } = node;

                const currencyCode =
                  node.parameters.currencyCode ??
                  charity?.settlementCurrencyCode ??
                  AcceptedCurrencyCode.Gbp;

                const dateCreated = formatDate(createDate);
                const defaultType = recurring
                  ? t('common.monthlyLabel')
                  : t('common.oneOffLabel');
                return (
                  <li key={donationLinkId} data-qa="donationLinkRow">
                    <ul // eslint-disable-line jsx-a11y/no-redundant-roles
                      className="donationLinks__row"
                      title={t('linkLibrary.donationLinks.listRowTitle')}
                      role="list"
                    >
                      <DonationLinkItem
                        label={`${t('linkLibrary.donationLinks.name')}:`}
                        value={name}
                        className="dl-name"
                        isDefault={isDefault}
                      />
                      <DonationLinkItem
                        label={`${t('linkLibrary.donationLinks.totalRaised')}:`}
                        value={getTotalRaised(currencyCode, totalRaised)}
                      />
                      <DonationLinkItem
                        label={`${t('linkLibrary.donationLinks.dateCreated')}:`}
                        value={dateCreated}
                      />
                      <DonationLinkItem
                        label={`${t('linkLibrary.donationLinks.defaultType')}:`}
                        value={defaultType}
                      />
                      <DonationLinkItem
                        label={`${t('linkLibrary.donationLinks.reference')}:`}
                        value={reference || '-'}
                      />
                      <DonationLinkItem
                        label="Author:"
                        value={`${author?.firstName ?? '-'} ${
                          author?.lastName ?? ''
                        }`}
                      />
                      <li className="donationLinks__row__item dl-actions">
                        <span className="dl-label"></span>
                        <LinkLibraryActionsMenu donationLink={node} />
                      </li>
                    </ul>
                  </li>
                );
              })}
            </React.Fragment>
          );
        })}
      </ul>
    );
  }

  if (isError && showPlaceholderDuringLoad) {
    return (
      <Refresh onRefresh={refetch}>
        <ul
          className="donationLinks__rows"
          title={t('linkLibrary.donationLinks.listTitle')}
        >
          <DonationLinksLoading isError />
        </ul>
      </Refresh>
    );
  }

  if (!showPlaceholderDuringLoad) return null;

  return (
    <ul
      className="donationLinks__rows"
      title={t('linkLibrary.donationLinks.listTitle')}
    >
      <DonationLinksLoading />
    </ul>
  );
};

type LoadMoreButtonProps = {
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  isError: boolean;
};

const LoadMoreButton: FunctionComponent<LoadMoreButtonProps> = ({
  onClick,
  disabled,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <button
        type="button"
        className={classNames(
          'jg-btn jg-btn--ghost jg-space-phmd loadMore__button',
          {
            'jg-space-pvxs': isLoading,
          },
        )}
        disabled={disabled}
        onClick={onClick}
      >
        {isLoading ? (
          <Loader
            colour="grey"
            className="loadMore__loader"
            accessibleLabel={t('common.loading')}
          />
        ) : disabled ? (
          t('linkLibrary.donationLinks.noMoreLinks')
        ) : (
          t('linkLibrary.donationLinks.loadMore')
        )}
      </button>
      {isError && (
        <div className="jg-space-mtsm">
          <span
            role="alert"
            className="jg-form-error-copy jg-display-ib jg-space-ptn"
          >
            {t('linkLibrary.donationLinks.loadError')}
          </span>
        </div>
      )}
    </>
  );
};

const LinkLibraryDonationLinks: FunctionComponent = () => {
  const [showArchived, setShowArchived] = useState(false);
  const auth = useContext(AuthContext);

  const {
    data,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteDonationLinksQuery(
    createGraphQlClient(auth),
    {
      input: { first: 4, archived: showArchived },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage?.donationLinks?.pageInfo.endCursor) return;
        return {
          input: {
            first: 4,
            after: lastPage?.donationLinks?.pageInfo.endCursor,
            archived: showArchived,
          },
        };
      },
    },
  );
  const {
    state: {
      isShareModalOpen,
      donationLink,
      isViewModalOpen,
      isArchiveModalOpen,
      isRestoreModalOpen,
    },
    dispatch,
  } = useDonationLinkContext();
  const { t } = useTranslation();

  const isEmptyList = data?.pages[0].donationLinks?.edges.length === 0;

  return (
    <>
      <fieldset className="donationLinks__selector">
        <legend className="jg-sr-only">
          {t('donationLinks.selector.legend')}
        </legend>
        <div className="donationLinks__selector__item">
          <label
            className={classNames('donationLinks__selector__label', {
              'donationLinks__selector__label--isActive': !showArchived,
            })}
          >
            <input
              type="radio"
              id="gc_selector_active"
              name="gc_selector"
              value="active"
              onChange={() => setShowArchived(false)}
              defaultChecked
              className="jg-sr-only"
            />
            <span aria-hidden>
              {t('donationLinks.selector.givingCheckout')}
            </span>
            <span className="jg-sr-only">
              {t('donationLinks.selector.activeCheckoutLinks')}
            </span>
          </label>
        </div>
        <div className="donationLinks__selector__item">
          <label
            className={classNames('donationLinks__selector__label', {
              'donationLinks__selector__label--isActive': showArchived,
            })}
          >
            <input
              type="radio"
              id="gc_selector_archived"
              name="gc_selector"
              value="archived"
              onChange={() => setShowArchived(true)}
              className="jg-sr-only"
            />
            <span aria-hidden>{t('donationLinks.selector.archived')}</span>
            <span className="jg-sr-only">
              {t('donationLinks.selector.archivedCheckoutLinks')}
            </span>
          </label>
        </div>
      </fieldset>
      <div className="donationLinks" data-qa="donationLinks">
        <div className="donationLinks__header" aria-hidden>
          <div className="donationLinks__header__item dl-name">
            {t('linkLibrary.donationLinks.name')}
          </div>
          <div className="donationLinks__header__item dl-total">
            {t('linkLibrary.donationLinks.totalRaised')}
          </div>
          <div className="donationLinks__header__item dl-date">
            {t('linkLibrary.donationLinks.dateCreated')}
          </div>
          <div className="donationLinks__header__item dl-type">
            {t('linkLibrary.donationLinks.defaultType')}
          </div>
          <div className="donationLinks__header__item dl-ref">
            {t('linkLibrary.donationLinks.reference')}
          </div>
          <div className="donationLinks__header__item dl-author">
            {t('linkLibrary.donationLinks.author')}
          </div>
          <div className="donationLinks__header__item"></div>
        </div>
        {isEmptyList && showArchived ? (
          <div className="donationLinks__noResultsRow">
            {t('linkLibrary.donationLinks.noArchivedLinks')}
          </div>
        ) : (
          <DonationLinkRows
            showPlaceholderDuringLoad={isFetchingNextPage ? false : true}
            showArchived={showArchived}
          />
        )}
      </div>
      {!isEmptyList && (
        <div className="jg-text--center jg-space-mtms">
          <LoadMoreButton
            onClick={isError ? refetch : fetchNextPage}
            disabled={!hasNextPage}
            isLoading={isFetching}
            isError={isError && !isFetching}
          />
        </div>
      )}
      <SimpleSharableLinkModal
        isOpen={isShareModalOpen}
        url={donationLink?.url}
        linkId={donationLink?.donationLinkId}
        isDefault={Boolean(donationLink?.isDefault)}
        onRequestClose={() =>
          dispatch({
            type: 'closeShareModal',
          })
        }
        hideFooter
      />
      <DonationLinkViewModal
        isOpen={isViewModalOpen}
        onRequestClose={() => dispatch({ type: 'closeViewModal' })}
        donationLink={donationLink}
      />
      <DonationLinkArchiveModal
        isOpen={isArchiveModalOpen}
        onRequestClose={() => dispatch({ type: 'closeArchiveModal' })}
        donationLink={donationLink}
      />
      <DonationLinkRestoreModal
        isOpen={isRestoreModalOpen}
        onRequestClose={() => dispatch({ type: 'closeRestoreModal' })}
        donationLink={donationLink}
      />
    </>
  );
};

export default LinkLibraryDonationLinks;
